import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { CheckCircle2, XCircle, AlertCircle, RefreshCw, Database, Server, Plug, Users as UsersIcon } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const StatusDot = ({ ok }) => (
  <span className={`inline-block w-2.5 h-2.5 rounded-full ${ok ? 'bg-emerald-500 animate-pulse' : 'bg-red-500'}`} />
);

const StatusBadge = ({ ok, label }) => (
  <span
    className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${
      ok
        ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300'
        : 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300'
    }`}
  >
    {ok ? <CheckCircle2 className="h-3.5 w-3.5" /> : <XCircle className="h-3.5 w-3.5" />}
    {label}
  </span>
);

const StatusPage = () => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [lastFetched, setLastFetched] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchStatus = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API}/admin/status`, { timeout: 10000 });
      setData(res.data);
      setLastFetched(new Date());
    } catch (e) {
      setError(e.message || 'Fetch error');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
    const interval = setInterval(fetchStatus, 15000); // refresh every 15s
    return () => clearInterval(interval);
  }, [fetchStatus]);

  const allOk = data?.ok && !error;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-purple-50/30 to-cyan-50/30 dark:from-gray-900 dark:via-gray-900 dark:to-gray-900 p-6" data-testid="status-page">
      <div className="max-w-4xl mx-auto">
        <header className="flex items-start justify-between gap-4 mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-3">
              <StatusDot ok={allOk} />
              OpenSapiens · Status
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              État en direct de l'infrastructure (rafraîchi toutes les 15 s)
              {lastFetched && ` · dernière mise à jour : ${lastFetched.toLocaleTimeString('fr-CH')}`}
            </p>
          </div>
          <button
            data-testid="status-refresh-btn"
            onClick={fetchStatus}
            disabled={loading}
            className="inline-flex items-center gap-2 px-3 py-2 text-sm bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl hover:shadow"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} /> Rafraîchir
          </button>
        </header>

        {error && (
          <div className="p-4 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-900/40 text-sm text-red-700 dark:text-red-300 mb-6 flex gap-3" data-testid="status-fetch-error">
            <AlertCircle className="h-5 w-5 shrink-0" />
            <div>
              <p className="font-semibold">Impossible d'atteindre le backend</p>
              <p className="mt-1 text-red-600/80 dark:text-red-300/80">{error}</p>
              <p className="mt-2 text-xs">Si l'erreur persiste, c'est probablement un problème Cloudflare/Kubernetes. Voir <code>/app/docs/emergent_support_email_template.md</code>.</p>
            </div>
          </div>
        )}

        <div className="grid sm:grid-cols-2 gap-4">
          {/* Backend */}
          <section className="p-5 rounded-2xl bg-white dark:bg-gray-800 border border-gray-200/70 dark:border-gray-700/70" data-testid="status-backend-card">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                <Server className="h-4 w-4" /> Backend
              </h2>
              <StatusBadge ok={!!data?.backend} label={data?.backend ? 'Online' : 'Offline'} />
            </div>
            <div className="space-y-1 text-sm text-gray-600 dark:text-gray-300">
              <p>Démarré à : <span className="font-mono text-xs">{data?.backend?.uptime_started_at || '—'}</span></p>
              <p>Environnement : <span className="font-mono text-xs">{data?.environment?.python_env || '—'}</span></p>
            </div>
          </section>

          {/* Database */}
          <section className="p-5 rounded-2xl bg-white dark:bg-gray-800 border border-gray-200/70 dark:border-gray-700/70" data-testid="status-db-card">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                <Database className="h-4 w-4" /> MongoDB
              </h2>
              <StatusBadge ok={!!data?.database?.reachable} label={data?.database?.reachable ? 'Connected' : 'Down'} />
            </div>
            <div className="space-y-1 text-sm text-gray-600 dark:text-gray-300">
              <p>Base : <span className="font-mono text-xs">{data?.environment?.mongo_db_name || '—'}</span></p>
              <p>Latence : {data?.database?.latency_ms ? `${data.database.latency_ms} ms` : '—'}</p>
              {data?.database?.error && (
                <p className="text-red-600 dark:text-red-400 text-xs mt-2">{data.database.error}</p>
              )}
            </div>
          </section>

          {/* Integrations */}
          <section className="p-5 rounded-2xl bg-white dark:bg-gray-800 border border-gray-200/70 dark:border-gray-700/70" data-testid="status-integrations-card">
            <h2 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2 mb-3">
              <Plug className="h-4 w-4" /> Intégrations
            </h2>
            <div className="space-y-2">
              {data?.integrations && Object.entries(data.integrations).map(([name, info]) => (
                <div key={name} className="flex items-center justify-between text-sm">
                  <span className="capitalize text-gray-700 dark:text-gray-300">{name.replace('_', ' ')}</span>
                  <span className="flex items-center gap-2">
                    {info.mode && <span className="text-[11px] text-gray-400 font-mono uppercase">{info.mode}</span>}
                    <StatusBadge ok={!!info.configured} label={info.configured ? 'Configured' : 'Missing'} />
                  </span>
                </div>
              ))}
            </div>
          </section>

          {/* Counts */}
          <section className="p-5 rounded-2xl bg-white dark:bg-gray-800 border border-gray-200/70 dark:border-gray-700/70" data-testid="status-counts-card">
            <h2 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2 mb-3">
              <UsersIcon className="h-4 w-4" /> Données
            </h2>
            <div className="grid grid-cols-3 gap-3 text-center">
              <div>
                <p className="text-xs text-gray-500 dark:text-gray-400">Utilisateurs</p>
                <p className="text-xl font-bold text-gray-900 dark:text-white">{data?.counts?.users ?? '—'}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 dark:text-gray-400">Sessions</p>
                <p className="text-xl font-bold text-gray-900 dark:text-white">{data?.counts?.active_sessions ?? '—'}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 dark:text-gray-400">Posts</p>
                <p className="text-xl font-bold text-gray-900 dark:text-white">{data?.counts?.posts ?? '—'}</p>
              </div>
            </div>
          </section>
        </div>

        {/* Environment box */}
        <div className="mt-6 p-4 rounded-2xl bg-gray-900/90 dark:bg-black text-gray-200 font-mono text-xs">
          <p className="text-gray-400 uppercase tracking-wider text-[10px] mb-1">Raw payload</p>
          <pre className="overflow-x-auto whitespace-pre-wrap" data-testid="status-raw-json">
            {data ? JSON.stringify(data, null, 2) : '(no data)'}
          </pre>
        </div>

        <p className="mt-6 text-xs text-center text-gray-500">
          Cette page est volontairement accessible sans authentification pour permettre
          un monitoring externe. Ne jamais y afficher de secrets.
        </p>
      </div>
    </div>
  );
};

export default StatusPage;
